/**
 * Step Navigation
 *
 * Renders the back button + action buttons for form steps.
 * Lives outside the AnimatePresence boundary so buttons
 * remain stable during step transitions.
 */

"use client";

import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useInsuranceConnectorSend } from "../insurance-connector-provider";
import {
  INSURANCE_STEP_FORM_ID,
  type StepAction,
  type StepConfig,
} from "../step-config";

export function StepNavigation({ config }: { config: StepConfig }) {
  const send = useInsuranceConnectorSend();

  return (
    <div className="flex gap-3 md:gap-4 justify-end md:justify-between items-center pt-3 md:pt-0">
      {config.showBackButton ? (
        <motion.button
          type="button"
          onClick={() => send({ type: "BACK" })}
          className="hidden md:flex border border-black rounded-full aspect-square w-[62px] h-[62px] items-center justify-center flex-shrink-0"
          aria-label="Go back"
          initial={{ backgroundColor: "transparent", color: "#000000" }}
          whileHover={{ backgroundColor: "#000000", color: "#ffffff" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <ArrowLeft className="h-6 w-6" />
        </motion.button>
      ) : (
        <div className="hidden md:block" />
      )}

      <div className="flex flex-row gap-2">
        {config.actions.map((action) => (
          <ActionButton key={action.label} action={action} />
        ))}
      </div>
    </div>
  );
}

function ActionButton({ action }: { action: StepAction }) {
  const send = useInsuranceConnectorSend();
  const t = useTranslations("InsuranceConnector");
  const isOutline = action.variant === "outline";

  const className = isOutline
    ? "inline-flex items-center justify-center rounded-full px-[53px] md:px-[48px] py-5 md:py-[20px] text-base md:text-xl font-medium border border-black/40"
    : "inline-flex items-center justify-center w-fit rounded-full px-[53px] md:px-[48px] py-5 md:py-[20px] text-base md:text-xl font-medium text-white";

  const motionProps = isOutline
    ? {
        initial: { backgroundColor: "transparent", color: "#000000" },
        whileHover: { backgroundColor: "#000000", color: "#ffffff" },
        transition: { duration: 0.3, ease: "easeInOut" as const },
      }
    : {
        initial: { backgroundColor: "#000000" },
        whileHover: { backgroundColor: "#00A3FF" },
        transition: { duration: 0.3, ease: "easeInOut" as const },
      };

  if (action.type === "submit") {
    return (
      <motion.button
        type="submit"
        form={INSURANCE_STEP_FORM_ID}
        className={className}
        {...motionProps}
      >
        {t(action.label)}
      </motion.button>
    );
  }

  return (
    <motion.button
      type="button"
      className={className}
      onClick={() => send({ type: action.event })}
      {...motionProps}
    >
      {t(action.label)}
    </motion.button>
  );
}
